import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { siteWide } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { Device, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import BackgroundStyles from '@stories/Components/Misc/Modal/Background/Background.styles';
import { m } from 'framer-motion';
import { rgba } from 'polished';
import styled from 'styled-components';
import CtaBlockStyles from '../CtaBlock/CtaBlock.styles';
import GroupLinkStyles from '../GroupLink/GroupLink.styles';
import HeaderStyles from '../Header.styles';
const Container = styled.nav `
  display: flex;
  align-items: center;
  gap: ${fluid(18, 32)};
  height: 100%;

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const TopLink = styled(m.div) `
  height: 100%;
`;
const TopLinkAnchor = styled(m.a) `
  ${fonts.poppins.extraBold};

  display: flex;
  position: relative;
  gap: 6px;
  align-items: center;
  font-size: 16px;
  color: ${brand.primary.white};
  text-decoration: none;
  height: 100%;

  @media ${until(Device.DesktopLarge)} {
    font-size: 14px;
  }

  &:focus {
    outline: none;
  }

  [data-solid='true'] & {
    color: ${brand.primary.black};
  }
`;
const Underline = styled(m.span) `
  position: absolute;
  bottom: 0;
  left: 0;
  height: 6px;
  width: 100%;
  border-radius: 100px;
  background-color: ${brand.primary.orange};
`;
const TopLinkIcon = styled(m.span) `
  width: 8px;
  height: 4px;
  min-width: 8px;

  svg {
    display: block;
    width: 100%;
  }
`;
const Dropdown = styled(m.div) `
  ${scrollbars};

  --columnGap: ${fluid(32, 56)};
  --rowGap: ${fluid(24, 40)};

  overflow: hidden;
  position: fixed;
  z-index: ${Layers.Navigation};
  top: var(--headerHeight);
  left: 0;
  max-height: calc(100vh - var(--headerHeight));
  width: 100%;
  background-color: ${brand.primary.white};
  box-shadow: 0px 5px 10px -3px ${rgba(brand.primary.black, 0.33)};
  cursor: default;

  [data-has-announcement-bar='true'] & {
    top: calc(var(--headerHeight) + var(--announcementBarHeight));
    max-height: calc(100vh - (var(--headerHeight) + var(--announcementBarHeight)));
  }

  ${HeaderStyles.Shadow} {
    position: absolute;
    top: -1px;
    opacity: 1;
  }
`;
const Bg = styled(BackgroundStyles.Background) `
  position: fixed;
  z-index: ${Layers.Navigation - 1};
  top: var(--headerHeight);
  height: calc(100vh - var(--headerHeight));
  height: calc(100dvh - var(--headerHeight));

  [data-has-announcement-bar='true'] & {
    top: calc(var(--headerHeight) + var(--announcementBarHeight));
    height: calc(100vh - (var(--headerHeight) + var(--announcementBarHeight)));
  }
`;
const RightColumn = styled(m.div) `
  grid-row: 1 / span 1;
  grid-column: 2 / span 1;
  display: grid;
  gap: var(--rowGap);
  margin-bottom: auto;
`;
const Inner = styled.div `
  ${siteWide};

  display: grid;
  gap: var(--rowGap) calc(var(--columnGap) * 2);
  grid-template-columns: 0.75fr 1fr;
  padding-top: ${fluid(26, 32)};
  padding-bottom: ${fluid(32, 38)};

  &:has(${CtaBlockStyles.Container}) {
    grid-template-rows: auto 1fr;
  }

  &:has(${RightColumn} > ${GroupLinkStyles.GroupLinkBlock}) {
    grid-template-columns: 1fr 3fr;
  }

  ${CtaBlockStyles.Container} {
    grid-row: 2;
  }
`;
const LeftColumn = styled.ul `
  position: relative;
  display: flex;
  flex-direction: column;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: calc(100% + var(--columnGap));
    height: 100%;
    width: 2px;
    background-color: ${brand.grey.light};
  }

  ${Inner}:has(${CtaBlockStyles.Container}) & {
    grid-row: 1 / span 2;
  }

  > ${GroupLinkStyles.GroupLinkBlock} {
    margin-bottom: ${fluid(32, 66)};
  }
`;
const LeftTitle = styled.li `
  ${fonts.poppins.bold};

  font-size: ${fluid(20, 22)};
  line-height: ${fluid(28, 30)};
  margin-bottom: 24px;
`;
const LeftItem = styled.li `
  &:last-of-type {
    margin-bottom: ${fluid(32, 66)};
  }
`;
const LinkColumns = styled.div `
  --arrowColour: ${brand.primary.orange};

  display: grid;
  gap: calc(var(--columnGap) * 0.66) var(--columnGap);

  /* Determine how many columns based on amount of children (max 3) */
  &:has(> *:nth-last-child(2)) {
    grid-template-columns: repeat(2, 1fr);
  }

  &:has(> *:nth-last-child(3)) {
    grid-template-columns: repeat(3, 1fr);
  }
`;
const LinkColumn = styled.ul `
  display: flex;
  flex-direction: column;
  gap: ${fluid(16, 20)};
`;
const LinkColumnTitle = styled.li `
  ${fonts.poppins.extraBold};

  font-size: ${fluid(18, 20)};
  color: ${brand.grey.g200};
  border-bottom: 1px solid currentColor;
  padding: 0 0 ${fluid(12, 16)};
  margin: 0 0 4px;
`;
const ColumnLink = styled.a `
  ${fonts.poppins.extraBold};

  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  text-decoration: none;
  color: ${brand.primary.black};

  svg {
    ${transition('transform')};

    display: block;
    color: var(--arrowColour);
    width: 14px;
  }

  &:focus,
  &:hover {
    svg {
      transform: translateX(3px);
    }
  }
`;
const DesktopNavigationStyles = {
    Bg,
    ColumnLink,
    Container,
    Dropdown,
    Inner,
    LeftColumn,
    LeftItem,
    LeftTitle,
    LinkColumn,
    LinkColumns,
    LinkColumnTitle,
    RightColumn,
    TopLink,
    TopLinkIcon,
    Underline,
    TopLinkAnchor,
};
export default DesktopNavigationStyles;
